import { Label } from "@/components/podkit/forms/Label";
import { cn } from "@/components/podkit/lib/cn";
import {
    SelectContentCombobox,
    SelectItemCombobox,
    SelectTrigger,
    SelectValue,
} from "@/components/podkit/select/Select";
import { getRepoUrlFromInitializer } from "@/queries/environment-queries";
import type { PlainProject } from "@/queries/project-queries";
import { ComboboxProvider } from "@ariakit/react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { startTransition, useId, useMemo, useState, type FC } from "react";

/**
 * No project selected means that the user has not selected a project. It's an explicit choice, not a default.
 */
export const NO_PROJECT_SELECTED = "no-project";

function valueFromSelection(selection: PlainProject): string {
    if (!selection) {
        return NO_PROJECT_SELECTED;
    }

    return `${selection.id ?? "unknown"}`;
}
function selectionFromValue(projects: PlainProject[], value: string): PlainProject | undefined {
    if (value === NO_PROJECT_SELECTED) {
        return undefined;
    }

    return projects.find((v) => v?.id == value);
}

type ProjectSelectProps = {
    projects: PlainProject[];
    label: string;
    name: string;
    value?: PlainProject;
    loading: boolean;
    disabled: boolean;
    onChange: (value: PlainProject | undefined) => void;
};
/**
 * Like EnvironmentClassSelect, but for projects. The component also offers an option to not select a project, which is consumed as `undefined` in the parent component.
 */
export const ProjectSelect: FC<ProjectSelectProps> = ({ projects, label, value, loading, disabled, onChange }) => {
    const elId = useId();

    const [open, setOpen] = useState(false);
    const [searchValue, setSearchValue] = useState("");

    const hasProjects = projects.length > 0;

    const matches = useMemo(() => {
        if (!searchValue) return projects;

        const words = searchValue.split(" ");
        const matches = projects.filter((v) => {
            const label = `${v?.metadata?.name ?? ""}`.toLowerCase();
            return words.reduce((acc, word) => acc && label.includes(word), true);
        });

        // Radix Select does not work if we don't render the selected item, so we
        // make sure to include it in the list of matches.
        const vv = value && valueFromSelection(value);
        if (value !== undefined) {
            const selectedEnvironmentType = projects.find((v) => valueFromSelection(v) == vv);
            if (selectedEnvironmentType && !matches.includes(selectedEnvironmentType)) {
                matches.push(selectedEnvironmentType);
            }
        }
        return matches;
    }, [projects, value, searchValue]);

    return (
        <div className="flex items-center space-x-2">
            <div className="flex w-full flex-col gap-2">
                <Label htmlFor={elId} className="font-medium">
                    {label}
                </Label>
                <SelectPrimitive.Root
                    value={value ? valueFromSelection(value) : NO_PROJECT_SELECTED}
                    onValueChange={(s) => {
                        onChange(selectionFromValue(projects, s));
                    }}
                    open={open}
                    onOpenChange={setOpen}
                    disabled={loading || disabled || !hasProjects}
                >
                    <ComboboxProvider
                        open={open}
                        setOpen={setOpen}
                        resetValueOnHide
                        includesBaseElement={false}
                        setValue={(value) => {
                            startTransition(() => {
                                setSearchValue(value.toLowerCase());
                            });
                        }}
                    >
                        <SelectTrigger
                            id={elId}
                            data-testid="project-select-trigger"
                            aria-label="Project"
                            loading={loading}
                            className="flex flex-row items-center justify-center"
                        >
                            {/* A <span style="pointer-events: none;" /> wraps our value if the item is disabled which breaks out layout
                            As we have no control over the span we instead style it using pseudo selectors ↓ */}
                            <div className="flex h-10 w-11/12 items-center [&>span]:flex [&>span]:w-full">
                                <SelectValue
                                    placeholder={
                                        <span className="flex flex-col justify-center">
                                            {hasProjects ? "No project selected" : "No available projects"}
                                        </span>
                                    }
                                />
                            </div>
                        </SelectTrigger>

                        <SelectContentCombobox inputPlaceholder="Search projects">
                            <SelectItemCombobox
                                value={NO_PROJECT_SELECTED}
                                className="w-full border-b border-border-light py-3 pl-2 pr-8"
                                asChild
                            >
                                <div className="flex w-full flex-row items-center justify-between">
                                    <span className="text-base font-bold text-content-primary">No project</span>
                                </div>
                            </SelectItemCombobox>
                            {matches.map((project) => {
                                const id = valueFromSelection(project);
                                const projectContextUrl = getRepoUrlFromInitializer(project.initializer);

                                return (
                                    <SelectItemCombobox
                                        key={`${project.id ?? ""}-${id}`}
                                        value={id}
                                        className={cn(
                                            "group w-full border-b border-border-light px-3 pl-2 pr-8 last:border-b-0",
                                            // Overwriting the default "disabled" behavior as we don't want to apply
                                            // opacity to the borders.
                                            "data-[disabled]:opacity-100",
                                        )}
                                        asChild
                                    >
                                        <div className="flex w-full flex-row items-center justify-between">
                                            <div className={cn("flex w-full flex-col items-start")}>
                                                <div className="flex w-full gap-1">
                                                    <span
                                                        title={project?.metadata?.name}
                                                        className="overflow-hidden text-ellipsis whitespace-nowrap text-base font-bold text-content-primary group-data-[disabled]:text-content-tertiary"
                                                    >
                                                        {project?.metadata?.name}
                                                    </span>
                                                </div>
                                                <div className="flex w-full gap-1">
                                                    <span
                                                        className="overflow-hidden text-ellipsis whitespace-nowrap text-left text-content-tertiary"
                                                        title={projectContextUrl}
                                                    >
                                                        {projectContextUrl}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </SelectItemCombobox>
                                );
                            })}
                        </SelectContentCombobox>
                    </ComboboxProvider>
                </SelectPrimitive.Root>
            </div>
        </div>
    );
};
